<template>
	<div class="d-inline-block token-image" v-if="mint">
		<div v-if="link" class="flex items-start gap-1 relative">
			<div class="flex items-center">
				<router-link class="d-inline-block text-decoration-none" :to="hideLink ? `` : `/${mint?.toString()}`">
					<div v-if="showImage" class="token-img-outer" @mouseenter="handleMouseEnter" @mouseleave="showHoverView = false">
						<CircleProgressBar :percent="percent??0" class="mr-2" :plt="plt">
							<div class="token-img " :class="{'lg': large, 'sm': small, 'medium': medium}" ref="tokenImg">
								<img :src="getSrc()" alt="" loading="lazy" onload="this.style.opacity=1" class="w-full h-full rounded-full">
							</div>
						</CircleProgressBar>	
						<sup v-if="getVerified()"><i class="fa fa-star" title="Verified token"></i></sup>
					</div>
				</router-link>
				<div class="text-start">
					<div class="flex items-center gap-0.5">
						<router-link class="text-decoration-none" :to="hideLink ? `` : `/${mint?.toString()}`">
							<p v-if="showSymbol" class="mb-0">{{ getSymbol() }}</p>
						</router-link>
						<FavoritesStar v-if="!noFavorites" :token="favoritesToken"/>
						<slot name="symbols"></slot>
					</div>
					<router-link class="text-decoration-none" :to="hideLink ? `` : `/${mint?.toString()}`">
						<p v-if="showName" class="mb-0 grey small text-decoration-none" style="font-size: 0.7rem">{{ getName() }}</p>
						<p v-if="showAddress" class="mb-0">
							<AccountAddress token :names="false" :address="mint"></AccountAddress>
						</p>
					</router-link>
				</div>
			</div>
		</div>

		<div v-else class="token-link relative">
			<div v-if="showImage" class="token-img-outer" @mouseenter="handleMouseEnter" @mouseleave="showHoverView = false">
				<CircleProgressBar :percent="percent??0" class="mr-2" :plt="plt">
					<div class="token-img " :class="{'lg': large, 'sm': small, 'medium': medium}" ref="tokenImg">
						<img :src="getSrc()" alt="" loading="lazy" onload="this.style.opacity=1">
					</div>
				</CircleProgressBar>
				<sup v-if="getVerified()"><i class="fa fa-star" title="Verified token"></i></sup>
			</div>
			<div class="text-start">
				<div class="flex items-center gap-0.5">
					<p v-if="showSymbol" class="mb-0">{{ getSymbol() }}</p>
					<FavoritesStar v-if="!noFavorites" :token="favoritesToken"/>
					<slot name="symbols"></slot>
				</div>

				<p v-if="showName" class="mb-0 grey small text-decoration-none" style="font-size: 0.7rem">{{ getName() }}</p>
				<p v-if="showAddress" class="mb-0">
					<AccountAddress token :names="false" :address="mint"></AccountAddress>
				</p>
			</div>
		</div>


		<!-- Simple Hover Image -->

		<div class="enlarged-token-image" :style="hoverStyle" v-if="showHoverView">
			<img :src="getSrc()" alt="" loading="lazy" onload="this.style.opacity=1">
		</div>

	</div>
</template>

<script>
import {useAlertStore} from '@/pinia/alertStore';
import {useCacheStore} from '@/pinia/cacheStore';
import {getMintImageURL} from '@/shared';
import {mapStores} from 'pinia';
import AccountAddress from "@/components/common/AccountAddress.vue";
import CircleProgressBar from "@/components/common/CircleProgressBar.vue";
import FavoritesStar from "@/components/common/favorites/FavoritesStar.vue";

export default {
	name: "TokenImage",
	components: {AccountAddress, CircleProgressBar, FavoritesStar},
	props: {
		noFavorites: {
			type: Boolean
		},
		progress: {
			type: Boolean
		},
		percent: {
			type: Number
		},
		plt: {
			type: String,
		},
		mint: {
			required: true
		},
		backup: {
			type: String,
		},
		backupName: {
			type: String,
		},
		backupSymbol: {
			type: String,
		},
		large: {
			type: Boolean,
		},
		medium: {
			type: Boolean,
		},
		small: {
			type: Boolean,
		},
		verified: {
			// type: Number,
		},
		link: {
			type: Boolean,
			default() {
				return true
			}
		},
		hideLink: {
			type: Boolean,
		},
		showSymbol: {
			type: Boolean,
			default() {
				return true
			}
		},
		showImage: {
			type: Boolean,
			default() {
				return true
			}
		},
		showName: {
			type: Boolean,
		},
		showAddress: {
			type: Boolean,
		}
	},
	data() {
		return {
			metadata: {},
			showHoverView: false,
			showAbove: false,
			hoverStyle: {
				position: 'fixed',
				top: '0px',
				left: '0px'
			}
		}
	},
	computed: {
		...mapStores(useCacheStore, useAlertStore),
		favoritesToken() {
			if (!this.mint) return null;
			return {
				address: this.mint.toString(),
				name: this.getName(),
				verify: this.getVerified(),
				image: this.getSrc()
			};
		},
	},
	methods: {
		handleMouseEnter() {
			if (this.$refs.tokenImg) {
				const rect = this.$refs.tokenImg.getBoundingClientRect();

				// const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
				// const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
				const viewportHeight = window.innerHeight;

				const bottomSpace = viewportHeight - rect.bottom;
				this.showAbove = bottomSpace < 300;

				const top = this.showAbove ? rect.top - 310 : rect.bottom + 15;

				const left = rect.left + rect.width / 2;

				this.hoverStyle.top = `${top}px`;
				this.hoverStyle.left = `${left}px`;
			}

			this.showHoverView = true;
		},

		async getMetadata() {

			const token = this.cacheStore.tokenList[this.mint]
			if (token) {
				this.metadata = {
					name: token.name ?? "",
					symbol: token.symbol ?? "",
					image: getMintImageURL(this.mint)
				}
				return
			}

			this.metadata = await this.fetchMetadata()
		},

		async fetchMetadata() {
			return await this.cacheStore.cacheToken(this.mint)
		},

		getData: async function () {
			try {
				let token = this.cacheStore.tokenList[this.mint]

				if (!token) {
					console.log("Loading token metadata", this.mint)
					await this.cacheStore.cacheToken(this.mint)
				}
			} catch (e) {
				console.error(e)
				this.alertStore.error("Error to cache token", this.mint)
			}

		},

		getVerified: function () {
			return this.verified > 0 || this?.metadata?.verified || false
		},

		getSrc: function () {
			let mint = this.mint?.toString()
			if (mint === 'So11111111111111111111111111111111111111112')
				mint = 'FoGo111111111111111111111111111111111111112'

			return this.backup || `https://api.fogo.degencdn.com/v1/tokens/${mint}/image.jpg`
		},
		getSymbol: function () {
			if (this.backupSymbol) {
				return this.backupSymbol
			}

			let symbol = this.metadata?.symbol?.replace(/\0/g, "").substring(0, 12) || ""

			// Replace SOL with FOGO for the native token
			if ((symbol === "SOL" || symbol === "WSOL") && this.mint?.toString() === "So11111111111111111111111111111111111111112") {
				return "FOGO"
			}

			return symbol
		},
		getName: function () {
			if (this.backupName) {
				return this.backupName
			}

			let metaName = this.metadata?.name || ""

			if (metaName?.indexOf("\n") > -1)
				metaName = this.metadata?.name.split("\n")[0]

			if (metaName.length > 32)
				metaName = metaName.substring(0, 32)

			return metaName?.replace(/\0/g, "") || ""
		},

	},
	beforeMount() {
		if (!this.backupName || this.backupName === "" || !this.backupSymbol || this.backupSymbol === "") {
			this.getMetadata()
		} else {
			this.metadata = {
				name: this.backupName,
				symbol: this.backupSymbol,
				image: getMintImageURL(this.mint)
			}
		}
	},
	mounted() {
		//
	}
}
</script>

<style scoped>
a {
	text-decoration: none;
}

.token-link {
	display: flex;
	align-items: center;
}


.token-img {
	/*background: rgba(255, 255, 255, 0.1);*/
	border-radius: 9999px;
	display: inline-block;
	overflow: hidden;
	transition: filter 0.3s linear;

	height: 36px;
	width: 36px;
}

.token-img.lg {
	border-radius: 5px;
	height: 64px;
	width: 64px;
}

.token-img.sm {
	border-radius: 5px;
	height: 24px;
	width: 24px;
}

.token-img.medium {
	height: 48px;
	width: 48px;
}

.token-img:hover {
	filter: brightness(1.5);
}

img {
	height: 38px;
	width: 38px;
	opacity: 0;
	object-fit: cover;
	transition: opacity 0.3s linear;
}

.sm img {
	height: 24px;
	width: 24px;
}

.medium img {
	height: 48px;
	width: 48px;
}

.lg img {
	height: 64px;
	width: 64px;
}

.token-img-outer {
	display: flex;
	position: relative;
}

sup {
	font-size: 0.5rem;
	color: var(--bs-primary);
}

sup {
	position: absolute;
	top: 0;
	right: 0.25rem;
}


/* Enlarged token image on hover */
.enlarged-token-image {
	z-index: 1000;
	border-radius: 8px;
	box-shadow: 0 4px 10px rgba(0, 0, 0, 0.5);
	background-color: #1c1c28;
	border: 1px solid rgba(255, 255, 255, 0.1);
	overflow: hidden;
	max-width: 300px;
	max-height: 300px;
}

.enlarged-token-image img {
	width: auto;
	height: 100%;
	border-radius: 8px;
	object-fit: contain;
	object-position: center;
	display: block;
	opacity: 0;
	transition: opacity 0.3s linear;
}
</style>